import { AppNotification, NotificationType } from '../services/notificationService';

/** Several notifications of the same kind about the same thing, shown as one row. */
export interface NotificationGroup {
  key: string;
  type: NotificationType;
  /** The newest item; its target is where a click goes. */
  newest: AppNotification;
  items: AppNotification[];
  /** Distinct actors, newest first — empty when the items carry no actor. */
  actors: { userId: string; displayName: string; profilePhotoUrl?: string | null }[];
  unread: boolean;
  /** Composed headline, e.g. "Sarah, Tom and 3 others reacted to your dive at Blue Hole". */
  headline: string;
  /** Extra detail line (a comment excerpt), only when there is one item. */
  detail?: string;
}

/** Only kinds where "N people did the same thing to the same target" reads naturally. */
const GROUPABLE: ReadonlySet<NotificationType> = new Set<NotificationType>(['PostReacted', 'PostCommented', 'NewFollower', 'PostShared']);

/** Items more than this far apart (newest vs. oldest in the group) stay separate rows. */
const WINDOW_MS = 24 * 60 * 60 * 1000;

function groupKey(n: AppNotification): string | null {
  if (!GROUPABLE.has(n.type) || !n.actorDisplayName) return null;
  // Followers have no shared target (relatedEntityId is the follower) — they group by type alone.
  return n.type === 'NewFollower' ? n.type : `${n.type}|${n.relatedEntityId ?? ''}`;
}

/** "Sarah", "Sarah and Tom", "Sarah, Tom and 3 others". */
export function nameList(names: string[]): string {
  const first = (n: string) => n.split(' ')[0] || n;
  if (names.length === 0) return 'Someone';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${first(names[0])} and ${first(names[1])}`;
  const rest = names.length - 2;
  return `${first(names[0])}, ${first(names[1])} and ${rest} other${rest === 1 ? '' : 's'}`;
}

/** The part of a body after the actor's name: " reacted to your dive at Blue Hole." → "reacted to your dive at Blue Hole". */
function verbPhrase(n: AppNotification): string {
  let rest = n.body;
  if (n.actorDisplayName && rest.startsWith(n.actorDisplayName)) rest = rest.slice(n.actorDisplayName.length);
  rest = rest.trim();
  // Comments carry an excerpt after a colon — the group headline wants only the verb.
  if (n.type === 'PostCommented') {
    const colon = rest.indexOf(':');
    if (colon > 0) rest = rest.slice(0, colon);
  }
  return rest.replace(/[.\s]+$/, '');
}

function headlineFor(type: NotificationType, actors: string[], sample: AppNotification): string {
  const verb = verbPhrase(sample);
  if (actors.length === 0) return sample.title;
  if (!verb) return `${nameList(actors)} — ${sample.title}`;
  // One actor keeps the server's exact sentence shape; several swap the name for the list.
  return `${nameList(actors)} ${verb}`;
}

/**
 * Collapses a newest-first notification list into rows. Same (type, target) within 24 h
 * become one row with stacked actors; everything else is a row of its own. Rows that lack
 * actor data (older notifications, system notices) are passed through untouched.
 */
export function groupNotifications(notifications: AppNotification[]): NotificationGroup[] {
  const sorted = [...notifications].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const open = new Map<string, NotificationGroup>();
  const out: NotificationGroup[] = [];

  for (const n of sorted) {
    const key = groupKey(n);
    if (key) {
      const existing = open.get(key);
      if (existing && new Date(existing.newest.createdAt).getTime() - new Date(n.createdAt).getTime() <= WINDOW_MS) {
        existing.items.push(n);
        if (n.actorUserId && !existing.actors.some(a => a.userId === n.actorUserId)) {
          existing.actors.push({ userId: n.actorUserId, displayName: n.actorDisplayName!, profilePhotoUrl: n.actorProfilePhotoUrl });
        }
        existing.unread = existing.unread || !n.read;
        continue;
      }
    }
    const group: NotificationGroup = {
      key: key ? `${key}|${n.notificationId}` : n.notificationId,
      type: n.type,
      newest: n,
      items: [n],
      actors: n.actorUserId && n.actorDisplayName ? [{ userId: n.actorUserId, displayName: n.actorDisplayName, profilePhotoUrl: n.actorProfilePhotoUrl }] : [],
      unread: !n.read,
      headline: '',
    };
    if (key) open.set(key, group);
    out.push(group);
  }

  for (const g of out) {
    g.headline = g.actors.length > 0 ? headlineFor(g.type, g.actors.map(a => a.displayName), g.newest) : g.newest.title;
    if (g.items.length === 1) {
      // A single row keeps the body as its detail line unless the headline already says it all.
      const single = g.newest.body;
      g.detail = g.actors.length > 0 && single.replace(/[.\s]+$/, '') === `${g.actors[0].displayName} ${verbPhrase(g.newest)}` ? undefined : single;
    } else if (g.type === 'PostCommented') {
      // Show the newest comment excerpt under a grouped comment row.
      const colon = g.newest.body.indexOf(':');
      if (colon > 0) g.detail = `${g.actors[0]?.displayName.split(' ')[0] ?? 'Latest'}: ${g.newest.body.slice(colon + 1).trim()}`;
    }
  }
  return out;
}
